/* eslint-disable multiline-comment-style */
import type { Message } from 'ai';
import React, { type RefCallback, useEffect, useState, useRef } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { Workbench } from '~/components/workbench/Workbench.client';
import { classNames } from '~/utils/classNames';
import { Messages } from './Messages.client';
import { SendButton } from './SendButton.client';
import type { AI_Model, Framework, Language } from '~/lib/persistence/selectionStore';
import styles from './BaseChat.module.scss';
import { chatStore } from '~/lib/stores/chat';
import { PanelButton } from './OAuthButton';
import { OauthModal } from './OAuthModal';
import CodeIntegration from './CodeIntegration';
import { ProjectRulesModal } from './ProjectRulesModal';
import { Dropdown, type DropdownOption } from '~/components/ui/Dropdown';
import { DelayedTooltip } from '~/components/ui/DelayedTooltip';
import Logo from '~/assets/alpha-logo.png';
import { motion } from 'framer-motion';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { toast } from 'react-toastify';
import RightTopBar from './RightTopBar';
import { json } from '@remix-run/cloudflare';
import type { LoaderFunction } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import HelpMenu from '~/components/ui/HelpMenu';
import type { StoreFileAction } from '~/types/actions';
import GithubModal from './GithubModal';
import { isDev } from '~/utils/constants';

interface BaseChatProps {
  textareaRef?: React.RefObject<HTMLTextAreaElement> | undefined;
  messageRef?: RefCallback<HTMLDivElement> | undefined;
  scrollRef?: RefCallback<HTMLDivElement> | undefined;
  showChat?: boolean;
  chatStarted?: boolean;
  isStreaming?: boolean;
  promptSubmited?: boolean;
  messages?: Message[];
  enhancingPrompt?: boolean;
  promptEnhanced?: boolean;
  input?: string;
  handleStop?: () => void;
  sendMessage?: (event: React.UIEvent, messageInput?: string) => void;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  enhancePrompt?: () => void;
  attachedImage?: string | null;
  onAttachImage?: (imageData: string) => void;
  onRemoveAttachedImage?: () => void;
  setGithubModalIsOpen?: (isOpen: boolean) => void;
  isFlutter?: boolean;
  extractedDesign?: {
    image: string | null;
    fonts: string[];
    colors: string[];
    imageUrls: string[];
  } | null;
  onExtractComplete?: (data: { image: string | null; fonts: string[]; colors: string[]; imageUrls: string[] }) => void;
  storeFileActions?: (fileActions: StoreFileAction[]) => Promise<void>;
  projectRules?: string | null;
  onEditProjectRules?: () => void;
}

const EXAMPLE_PROMPTS = [
  { text: 'Build a responsive landing page with animations', icon: 'i-ph:layout' },
  { text: 'Create an interactive product carousel', icon: 'i-ph:circle' },
  { text: 'Design a marketing dashboard with analytics', icon: 'i-ph:list' },
  { text: 'Build a form with client-side validation', icon: 'i-ph:check-circle' },
  { text: 'Create a simple todo list app', icon: 'i-ph:check-square' },
  { text: 'Build a simple chat app', icon: 'i-ph:chat-centered-dots' },
];

const TEXTAREA_MIN_HEIGHT = 76;

type AuthFrontendEnvs = {
  NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
  NEXT_PUBLIC_NETLIFY_CLIENT_SECRET: string;
  NEXT_PUBLIC_NETLIFY_APP_URL: string;
  NEXT_PUBLIC_GITHUB_REPO: string;
};

// Loader function to expose environment variables
export const loader: LoaderFunction = async ({ context }) => {
  const cloudflareEnv = context.cloudflare.env;

  const env: AuthFrontendEnvs = {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID:
      process.env.NEXT_PUBLIC_NETLIFY_CLIENT_ID || cloudflareEnv.NEXT_PUBLIC_NETLIFY_CLIENT_ID,
    NEXT_PUBLIC_NETLIFY_CLIENT_SECRET:
      process.env.NEXT_PUBLIC_NETLIFY_CLIENT_SECRET || cloudflareEnv.NEXT_PUBLIC_NETLIFY_CLIENT_SECRET,
    NEXT_PUBLIC_NETLIFY_APP_URL: process.env.NEXT_PUBLIC_NETLIFY_APP_URL || cloudflareEnv.NEXT_PUBLIC_NETLIFY_APP_URL,
    NEXT_PUBLIC_GITHUB_REPO: process.env.NEXT_PUBLIC_GITHUB_REPO || cloudflareEnv.NEXT_PUBLIC_GITHUB_REPO,
  };

  return json(env);
};

export const BaseChat = React.forwardRef<HTMLDivElement, BaseChatProps>(
  (
    {
      textareaRef,
      messageRef,
      scrollRef,
      showChat = true,
      chatStarted = false,
      isStreaming = false,
      promptSubmited = false,
      enhancingPrompt = false,
      promptEnhanced = false,
      messages,
      input = '',
      sendMessage,
      handleInputChange,
      enhancePrompt,
      handleStop,
      attachedImage,
      onAttachImage,
      onRemoveAttachedImage,
      isFlutter,
      extractedDesign,
      onExtractComplete,
      storeFileActions,
      projectRules,
      onEditProjectRules,
      setGithubModalIsOpen,
    },
    ref,
  ) => {
    const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

    const [showSuccessIcon, setShowSuccessIcon] = useState(false);
    const [showImageSuccessIcon, setShowImageSuccessIcon] = useState(false);
    const [showFigmaSuccessIcon, setShowFigmaSuccessIcon] = useState(false);
    const [showFigmaErrorIcon, setShowFigmaErrorIcon] = useState(false);
    const [openApiSpec, setOpenApiSpec] = useState('');
    const [isCodeIntegrationOpen, setIsCodeIntegrationOpen] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const selectedFramework = selectionStore.get().framework;
    const netlifyConfig = useLoaderData<AuthFrontendEnvs>();

    const [oauthIsOpen, setOauthIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    console.log('input field data', input);

    useEffect(() => {
      if (attachedImage && !extractedDesign) {
        setShowImageSuccessIcon(true);
        setShowFigmaSuccessIcon(false);
        setShowFigmaErrorIcon(false);
      } else if (!attachedImage) {
        setShowImageSuccessIcon(false);
      }
    }, [attachedImage, extractedDesign]);

    useEffect(() => {
      if (extractedDesign?.image) {
        setShowFigmaSuccessIcon(true);
        setShowFigmaErrorIcon(false);
        setShowImageSuccessIcon(false);
      } else if (!extractedDesign?.image) {
        setShowFigmaSuccessIcon(false);
      }
    }, [extractedDesign]);

    useEffect(() => {
      // Load API spec from localStorage if exists
      const savedApiSpec = localStorage.getItem('dualite_api_data');

      if (savedApiSpec) {
        setOpenApiSpec(savedApiSpec);
        setShowSuccessIcon(true);
      }
    }, []);

    const handlePaste = (event: React.ClipboardEvent) => {
      const items = event.clipboardData?.items;

      if (!items) {
        return;
      }

      for (const item of items) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();

          if (!file) {
            continue;
          }

          const reader = new FileReader();

          reader.onload = (e) => {
            const base64Image = e.target?.result as string;
            onAttachImage?.(base64Image);
          };
          reader.readAsDataURL(file);
          break;
        }
      }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();

        reader.onload = (e) => {
          const base64Image = e.target?.result as string;
          onAttachImage?.(base64Image);

          if (showFigmaSuccessIcon) {
            onRemoveAttachedImage?.();
            setShowFigmaSuccessIcon(false);
          }

          setShowImageSuccessIcon(true);
        };
        reader.readAsDataURL(file);
      }

      event.target.value = '';
    };

    const FRAMEWORK_CONFIGS: Record<Framework, { languages: readonly Language[]; defaultLanguage: Language }> = {
      flutter: {
        languages: ['dart'] as const,
        defaultLanguage: 'dart',
      },
      react_tailwind: {
        languages: ['ts', 'js'] as const,
        defaultLanguage: 'ts',
      },
      react_chakra: {
        languages: ['ts', 'js'] as const,
        defaultLanguage: 'ts',
      },
      react_shadcn: {
        languages: ['ts'] as const,
        defaultLanguage: 'ts',
      },
      html_css: {
        languages: ['js'] as const,
        defaultLanguage: 'js',
      },
      html_tailwind: {
        languages: ['js'] as const,
        defaultLanguage: 'js',
      },
      angular_css: {
        languages: ['ts'] as const,
        defaultLanguage: 'ts',
      },
      vue_tailwind: {
        languages: ['ts'] as const,
        defaultLanguage: 'ts',
      },
      reactnative: {
        languages: ['ts'] as const,
        defaultLanguage: 'ts',
      },
    } as const;

    const isLanguageKey = (lang: string): lang is Language => {
      return ['ts', 'js', 'dart'].includes(lang);
    };

    const aiModelOptions: DropdownOption[] = [
      {
        value: 'dev-ai-model',
        label: 'Dev AI Model',
      },
      {
        value: 'prod-ai-model',
        label: 'Prod AI Model',
      },
      {
        value: 'dev-mock-ai-model',
        label: 'Dev Mock AI Model',
      },
    ];

    const frameworkOptions: DropdownOption[] = [
      {
        value: 'react_tailwind',
        label: 'React + Tailwind',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.10244 8.438C7.79656 8.61463 7.54991 8.87799 7.3937 9.19478C7.23748 9.51158 7.17871 9.86758 7.22481 10.2178C7.27091 10.568 7.41981 10.8966 7.65269 11.1622C7.88557 11.4278 8.19197 11.6183 8.53314 11.7098C8.87432 11.8012 9.23495 11.7895 9.56943 11.676C9.90392 11.5625 10.1972 11.3523 10.4123 11.0721C10.6274 10.7919 10.7545 10.4543 10.7777 10.1018C10.8008 9.74934 10.719 9.39793 10.5424 9.092C10.4252 8.88882 10.2691 8.71073 10.083 8.5679C9.8969 8.42507 9.6845 8.3203 9.45792 8.25956C9.23133 8.19883 8.995 8.18333 8.76243 8.21395C8.52985 8.24456 8.30559 8.3207 8.10244 8.438ZM16.1074 10.376C15.9901 10.242 15.8664 10.1077 15.7364 9.973C15.8271 9.877 15.9151 9.78133 16.0004 9.686C17.6054 7.882 18.2834 6.072 17.6554 4.985C17.0534 3.942 15.2624 3.631 13.0194 4.067C12.7988 4.11033 12.5791 4.159 12.3604 4.213C12.3184 4.069 12.2741 3.92567 12.2274 3.783C11.4664 1.49 10.2374 -0.000999497 8.98144 5.02707e-07C7.77744 5.02707e-07 6.61344 1.397 5.87044 3.558C5.79711 3.77133 5.72944 3.986 5.66744 4.202C5.52144 4.166 5.37477 4.13267 5.22744 4.102C2.86144 3.617 0.956441 3.937 0.329441 5.026C-0.271559 6.069 0.356441 7.776 1.85744 9.498C2.00677 9.668 2.16011 9.83467 2.31744 9.998C2.13144 10.1887 1.95644 10.379 1.79244 10.569C0.327441 12.267 -0.264559 13.945 0.335441 14.984C0.955441 16.058 2.83344 16.409 5.12044 15.959C5.30577 15.9223 5.49011 15.881 5.67344 15.835C5.74011 16.069 5.81377 16.3013 5.89444 16.532C6.63444 18.649 7.79144 20 8.99144 20C10.2314 20 11.4734 18.547 12.2264 16.341C12.2858 16.1663 12.3421 15.986 12.3954 15.8C12.6321 15.8587 12.8704 15.9107 13.1104 15.956C15.3134 16.373 17.0624 16.046 17.6614 15.006C18.2804 13.931 17.6414 12.129 16.1074 10.376ZM1.06944 5.452C1.45544 4.782 3.01244 4.52 5.05544 4.94C5.18611 4.96667 5.31911 4.99667 5.45444 5.03C5.25484 5.91238 5.11386 6.807 5.03244 7.708C4.29588 8.22632 3.59343 8.79149 2.92944 9.4C2.78344 9.24867 2.64111 9.09367 2.50244 8.935C1.21544 7.461 0.707441 6.081 1.06944 5.452ZM4.95644 11.18C4.44644 10.793 3.97144 10.397 3.54044 9.999C3.97044 9.603 4.44544 9.209 4.95544 8.823C4.93677 9.21567 4.92777 9.60867 4.92844 10.002C4.92844 10.3953 4.93777 10.788 4.95644 11.18ZM4.95644 15.12C4.08977 15.3139 3.19471 15.3458 2.31644 15.214C2.07571 15.1949 1.84142 15.1268 1.628 15.0138C1.41458 14.9008 1.22654 14.7454 1.07544 14.557C0.710441 13.927 1.18644 12.579 2.43944 11.127C2.59677 10.945 2.75944 10.7677 2.92744 10.595C3.59155 11.2075 4.29542 11.7754 5.03444 12.295C5.11714 13.2073 5.25944 14.1133 5.46044 15.007C5.29377 15.049 5.12544 15.087 4.95544 15.121M12.0554 7.082C11.7201 6.87067 11.3808 6.66633 11.0374 6.469C10.6988 6.27433 10.3564 6.08667 10.0104 5.906C10.5794 5.6662 11.1598 5.45428 11.7494 5.271C11.8822 5.86907 11.9843 6.47352 12.0554 7.082ZM6.67944 3.835C7.31544 1.985 8.25744 0.855 8.98344 0.855C9.75644 0.854 10.7604 2.073 11.4174 4.052C11.4601 4.18133 11.5001 4.31133 11.5374 4.442C10.675 4.70935 9.83116 5.03339 9.01144 5.412C8.19454 5.02852 7.35261 4.70077 6.49144 4.431C6.54944 4.231 6.61211 4.03233 6.67944 3.835ZM6.27944 5.259C6.8667 5.44328 7.44413 5.65756 8.00944 5.901C7.30811 6.26233 6.62544 6.656 5.96144 7.082C6.04144 6.444 6.14844 5.833 6.27944 5.259ZM5.96244 12.919C6.29377 13.1317 6.63011 13.336 6.97144 13.532C7.31944 13.732 7.67177 13.924 8.02844 14.108C7.459 14.3597 6.87688 14.5816 6.28444 14.773C6.14658 14.161 6.03912 13.5426 5.96244 12.92M11.4184 16.066C11.1532 16.9133 10.7334 17.7043 10.1804 18.399C10.0439 18.5981 9.86807 18.7672 9.66377 18.8958C9.45947 19.0245 9.23101 19.1099 8.99244 19.147C8.26344 19.147 7.33444 18.062 6.70244 16.251C6.62777 16.037 6.55911 15.821 6.49644 15.603C7.36175 15.3242 8.20704 14.9868 9.02644 14.593C9.85332 14.9742 10.7041 15.3012 11.5734 15.572C11.5254 15.738 11.4738 15.9027 11.4184 16.066ZM11.7804 14.742C11.1828 14.5558 10.5948 14.3402 10.0184 14.096C10.3578 13.918 10.6994 13.7297 11.0434 13.531C11.3968 13.327 11.7408 13.118 12.0754 12.904C12.0089 13.5213 11.9104 14.1348 11.7804 14.742ZM12.2274 9.999C12.2274 10.6063 12.2084 11.213 12.1704 11.819C11.6764 12.1537 11.1584 12.4777 10.6164 12.791C10.0764 13.1023 9.54411 13.3877 9.01944 13.647C8.46811 13.383 7.92744 13.0983 7.39744 12.793C6.87077 12.4897 6.35611 12.1667 5.85344 11.824C5.80677 11.2173 5.78344 10.61 5.78344 10.002C5.78344 9.39467 5.80611 8.78767 5.85144 8.181C6.85636 7.49413 7.91207 6.8847 9.00944 6.358C9.55344 6.62267 10.0878 6.90633 10.6124 7.209C11.1391 7.51167 11.6558 7.83133 12.1624 8.168C12.2058 8.77733 12.2274 9.38767 12.2274 9.999ZM13.1834 4.906C15.1054 4.533 16.5534 4.784 16.9164 5.413C17.3034 6.083 16.7494 7.561 15.3624 9.119C15.2858 9.205 15.2064 9.29133 15.1244 9.378C14.449 8.76718 13.7327 8.2032 12.9804 7.69C12.9048 6.79894 12.7695 5.91395 12.5754 5.041C12.7821 4.99033 12.9848 4.94533 13.1834 4.906ZM13.0534 8.791C13.5612 9.16121 14.0492 9.55775 14.5154 9.979C14.0513 10.4069 13.565 10.8102 13.0584 11.187C13.0738 10.791 13.0814 10.395 13.0814 9.999C13.0814 9.59633 13.0721 9.19367 13.0534 8.791ZM16.9224 14.58C16.5584 15.211 15.1544 15.474 13.2694 15.118C13.0534 15.0773 12.8321 15.0287 12.6054 14.972C12.7949 14.0878 12.9242 13.1917 12.9924 12.29C13.7431 11.7679 14.4572 11.1948 15.1294 10.575C15.2474 10.697 15.3594 10.8183 15.4654 10.939C16.0671 11.5921 16.5428 12.3509 16.8684 13.177C16.9727 13.3948 17.0312 13.6316 17.0405 13.8729C17.0498 14.1142 17.0096 14.3548 16.9224 14.58Z"
                fill="white"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'react_chakra',
        label: 'React + Chakra',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <path
                fill="currentColor"
                d="M12 0C5.352 0 0 5.352 0 12s5.352 12 12 12 12-5.352 12-12S18.648 0 12 0zm2.8 4.333c.13-.004.248.136.171.278l-3.044 5.58a.187.187 0 00.164.276h5.26c.17 0 .252.207.128.323l-9.22 8.605c-.165.154-.41-.063-.278-.246l4.364-6.021a.187.187 0 00-.151-.296H6.627a.187.187 0 01-.131-.32l8.18-8.123a.182.182 0 01.125-.056z"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'react_shadcn',
        label: 'React + Shadcn',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.2192 11.7842L11.7842 22.2192C11.6043 22.418 11.5078 22.6783 11.5145 22.9463C11.5212 23.2143 11.6307 23.4695 11.8202 23.6591C12.0098 23.8487 12.265 23.9581 12.533 23.9648C12.801 23.9716 13.0613 23.875 13.2602 23.6952L23.6952 13.2602C23.875 13.0613 23.9716 12.801 23.9648 12.533C23.9581 12.265 23.8487 12.0098 23.6591 11.8202C23.4695 11.6307 23.2143 11.5212 22.9463 11.5145C22.6783 11.5078 22.418 11.6043 22.2192 11.7842ZM20.1322 0.305163L0.305163 20.1322C0.12532 20.331 0.0287713 20.5913 0.0354842 20.8593C0.0421972 21.1273 0.151659 21.3825 0.34123 21.5721C0.530801 21.7617 0.78598 21.8711 1.05399 21.8778C1.322 21.8846 1.58234 21.788 1.78116 21.6082L21.6082 1.78116C21.788 1.58234 21.8846 1.322 21.8778 1.05399C21.8711 0.78598 21.7617 0.530801 21.5721 0.34123C21.3825 0.151659 21.1273 0.0421972 20.8593 0.0354842C20.5913 0.0287713 20.331 0.12532 20.1322 0.305163Z"
                fill="white"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'html_css',
        label: 'HTML + CSS',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <path
                fill="currentColor"
                d="M1.5 0h21l-1.91 21.563L11.977 24l-8.564-2.438L1.5 0zm7.031 9.75l-.232-2.718 10.059.003.23-2.622L5.412 4.41l.698 8.01h9.126l-.326 3.426-2.91.804-2.955-.81-.188-2.11H6.248l.33 4.171L12 19.351l5.379-1.443.744-8.157H8.531z"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'html_tailwind',
        label: 'HTML + Tailwind',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <path
                fill="currentColor"
                d="M12.001,4.8c-3.2,0-5.2,1.6-6,4.8c1.2-1.6,2.6-2.2,4.2-1.8c0.913,0.228,1.565,0.89,2.288,1.624 C13.666,10.618,15.027,12,18.001,12c3.2,0,5.2-1.6,6-4.8c-1.2,1.6-2.6,2.2-4.2,1.8c-0.913-0.228-1.565-0.89-2.288-1.624 C16.337,6.182,14.976,4.8,12.001,4.8z M6.001,12c-3.2,0-5.2,1.6-6,4.8c1.2-1.6,2.6-2.2,4.2-1.8c0.913,0.228,1.565,0.89,2.288,1.624 c1.177,1.194,2.538,2.576,5.512,2.576c3.2,0,5.2-1.6,6-4.8c-1.2,1.6-2.6,2.2-4.2,1.8c-0.913-0.228-1.565-0.89-2.288-1.624 C10.337,13.382,8.976,12,6.001,12z"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'angular_css',
        label: 'Angular + CSS',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <path
                fill="currentColor"
                d="M9.931 12.645h4.138l-2.07-4.908m0-7.737L.68 3.982l1.726 14.771L12 24l9.596-5.242L23.32 3.984 11.999.001zm7.064 18.31h-2.638l-1.422-3.503H8.996l-1.422 3.504h-2.64L12 2.65z"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'vue_tailwind',
        label: 'Vue + Tailwind',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <path
                fill="currentColor"
                d="M24,1.61H14.06L12,5.16,9.94,1.61H0L12,22.39ZM12,14.08,5.16,2.23H9.59L12,6.41l2.41-4.18h4.43Z"
              />
            </svg>
          </div>
        ),
      },
      {
        value: 'reactnative',
        label: 'React Native',
        icon: (
          <div className="w-5 h-5 flex items-center justify-center">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.10244 8.438C7.79656 8.61463 7.54991 8.87799 7.3937 9.19478C7.23748 9.51158 7.17871 9.86758 7.22481 10.2178C7.27091 10.568 7.41981 10.8966 7.65269 11.1622C7.88557 11.4278 8.19197 11.6183 8.53314 11.7098C8.87432 11.8012 9.23495 11.7895 9.56943 11.676C9.90392 11.5625 10.1972 11.3523 10.4123 11.0721C10.6274 10.7919 10.7545 10.4543 10.7777 10.1018C10.8008 9.74934 10.719 9.39793 10.5424 9.092C10.4252 8.88882 10.2691 8.71073 10.083 8.5679C9.8969 8.42507 9.6845 8.3203 9.45792 8.25956C9.23133 8.19883 8.995 8.18333 8.76243 8.21395C8.52985 8.24456 8.30559 8.3207 8.10244 8.438ZM16.1074 10.376C15.9901 10.242 15.8664 10.1077 15.7364 9.973C15.8271 9.877 15.9151 9.78133 16.0004 9.686C17.6054 7.882 18.2834 6.072 17.6554 4.985C17.0534 3.942 15.2624 3.631 13.0194 4.067C12.7988 4.11033 12.5791 4.159 12.3604 4.213C12.3184 4.069 12.2741 3.92567 12.2274 3.783C11.4664 1.49 10.2374 -0.000999497 8.98144 5.02707e-07C7.77744 5.02707e-07 6.61344 1.397 5.87044 3.558C5.79711 3.77133 5.72944 3.986 5.66744 4.202C5.52144 4.166 5.37477 4.13267 5.22744 4.102C2.86144 3.617 0.956441 3.937 0.329441 5.026C-0.271559 6.069 0.356441 7.776 1.85744 9.498C2.00677 9.668 2.16011 9.83467 2.31744 9.998C2.13144 10.1887 1.95644 10.379 1.79244 10.569C0.327441 12.267 -0.264559 13.945 0.335441 14.984C0.955441 16.058 2.83344 16.409 5.12044 15.959C5.30577 15.9223 5.49011 15.881 5.67344 15.835C5.74011 16.069 5.81377 16.3013 5.89444 16.532C6.63444 18.649 7.79144 20 8.99144 20C10.2314 20 11.4734 18.547 12.2264 16.341C12.2858 16.1663 12.3421 15.986 12.3954 15.8C12.6321 15.8587 12.8704 15.9107 13.1104 15.956C15.3134 16.373 17.0624 16.046 17.6614 15.006C18.2804 13.931 17.6414 12.129 16.1074 10.376ZM1.06944 5.452C1.45544 4.782 3.01244 4.52 5.05544 4.94C5.18611 4.96667 5.31911 4.99667 5.45444 5.03C5.25484 5.91238 5.11386 6.807 5.03244 7.708C4.29588 8.22632 3.59343 8.79149 2.92944 9.4C2.78344 9.24867 2.64111 9.09367 2.50244 8.935C1.21544 7.461 0.707441 6.081 1.06944 5.452ZM4.95644 11.18C4.44644 10.793 3.97144 10.397 3.54044 9.999C3.97044 9.603 4.44544 9.209 4.95544 8.823C4.93677 9.21567 4.92777 9.60867 4.92844 10.002C4.92844 10.3953 4.93777 10.788 4.95644 11.18ZM4.95644 15.12C4.08977 15.3139 3.19471 15.3458 2.31644 15.214C2.07571 15.1949 1.84142 15.1268 1.628 15.0138C1.41458 14.9008 1.22654 14.7454 1.07544 14.557C0.710441 13.927 1.18644 12.579 2.43944 11.127C2.59677 10.945 2.75944 10.7677 2.92744 10.595C3.59155 11.2075 4.29542 11.7754 5.03444 12.295C5.11714 13.2073 5.25944 14.1133 5.46044 15.007C5.29377 15.049 5.12544 15.087 4.95544 15.121M12.0554 7.082C11.7201 6.87067 11.3808 6.66633 11.0374 6.469C10.6988 6.27433 10.3564 6.08667 10.0104 5.906C10.5794 5.6662 11.1598 5.45428 11.7494 5.271C11.8822 5.86907 11.9843 6.47352 12.0554 7.082ZM6.67944 3.835C7.31544 1.985 8.25744 0.855 8.98344 0.855C9.75644 0.854 10.7604 2.073 11.4174 4.052C11.4601 4.18133 11.5001 4.31133 11.5374 4.442C10.675 4.70935 9.83116 5.03339 9.01144 5.412C8.19454 5.02852 7.35261 4.70077 6.49144 4.431C6.54944 4.231 6.61211 4.03233 6.67944 3.835ZM6.27944 5.259C6.8667 5.44328 7.44413 5.65756 8.00944 5.901C7.30811 6.26233 6.62544 6.656 5.96144 7.082C6.04144 6.444 6.14844 5.833 6.27944 5.259ZM5.96244 12.919C6.29377 13.1317 6.63011 13.336 6.97144 13.532C7.31944 13.732 7.67177 13.924 8.02844 14.108C7.459 14.3597 6.87688 14.5816 6.28444 14.773C6.14658 14.161 6.03912 13.5426 5.96244 12.92M11.4184 16.066C11.1532 16.9133 10.7334 17.7043 10.1804 18.399C10.0439 18.5981 9.86807 18.7672 9.66377 18.8958C9.45947 19.0245 9.23101 19.1099 8.99244 19.147C8.26344 19.147 7.33444 18.062 6.70244 16.251C6.62777 16.037 6.55911 15.821 6.49644 15.603C7.36175 15.3242 8.20704 14.9868 9.02644 14.593C9.85332 14.9742 10.7041 15.3012 11.5734 15.572C11.5254 15.738 11.4738 15.9027 11.4184 16.066ZM11.7804 14.742C11.1828 14.5558 10.5948 14.3402 10.0184 14.096C10.3578 13.918 10.6994 13.7297 11.0434 13.531C11.3968 13.327 11.7408 13.118 12.0754 12.904C12.0089 13.5213 11.9104 14.1348 11.7804 14.742ZM12.2274 9.999C12.2274 10.6063 12.2084 11.213 12.1704 11.819C11.6764 12.1537 11.1584 12.4777 10.6164 12.791C10.0764 13.1023 9.54411 13.3877 9.01944 13.647C8.46811 13.383 7.92744 13.0983 7.39744 12.793C6.87077 12.4897 6.35611 12.1667 5.85344 11.824C5.80677 11.2173 5.78344 10.61 5.78344 10.002C5.78344 9.39467 5.80611 8.78767 5.85144 8.181C6.85636 7.49413 7.91207 6.8847 9.00944 6.358C9.55344 6.62267 10.0878 6.90633 10.6124 7.209C11.1391 7.51167 11.6558 7.83133 12.1624 8.168C12.2058 8.77733 12.2274 9.38767 12.2274 9.999ZM13.1834 4.906C15.1054 4.533 16.5534 4.784 16.9164 5.413C17.3034 6.083 16.7494 7.561 15.3624 9.119C15.2858 9.205 15.2064 9.29133 15.1244 9.378C14.449 8.76718 13.7327 8.2032 12.9804 7.69C12.9048 6.79894 12.7695 5.91395 12.5754 5.041C12.7821 4.99033 12.9848 4.94533 13.1834 4.906ZM13.0534 8.791C13.5612 9.16121 14.0492 9.55775 14.5154 9.979C14.0513 10.4069 13.565 10.8102 13.0584 11.187C13.0738 10.791 13.0814 10.395 13.0814 9.999C13.0814 9.59633 13.0721 9.19367 13.0534 8.791ZM16.9224 14.58C16.5584 15.211 15.1544 15.474 13.2694 15.118C13.0534 15.0773 12.8321 15.0287 12.6054 14.972C12.7949 14.0878 12.9242 13.1917 12.9924 12.29C13.7431 11.7679 14.4572 11.1948 15.1294 10.575C15.2474 10.697 15.3594 10.8183 15.4654 10.939C16.0671 11.5921 16.5428 12.3509 16.8684 13.177C16.9727 13.3948 17.0312 13.6316 17.0405 13.8729C17.0498 14.1142 17.0096 14.3548 16.9224 14.58Z"
                fill="white"
              />
            </svg>
          </div>
        ),
      },
    ];

    const handleFigmaExtractError = (error: { image: string | null; errorType: 'size' | 'dimension' }) => {
      if (error.image) {
        onAttachImage?.(error.image);
        setShowFigmaErrorIcon(true);
        setShowFigmaSuccessIcon(false);
        setShowImageSuccessIcon(false);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          return;
        }

        event.preventDefault();

        if (showFigmaErrorIcon) {
          toast.error('Please remove the invalid Figma design before sending');
          return;
        }

        sendMessage?.(event);
      }
    };

    return (
      <div
        ref={ref}
        className={classNames(styles.BaseChat, 'relative flex flex-col h-fit w-full bg-transparent')}
        data-chat-visible={showChat}
      >
        {/* <ClientOnly>{() => <Menu />}</ClientOnly> */}
        <div className={classNames('flex w-full', 'mt-5 h-full')}>
          <ClientOnly>
            {() => (
              <Workbench
                chatStarted={chatStarted}
                isStreaming={isStreaming}
                isFlutter={selectionStore.get().framework === 'flutter'}
                storeFileActions={storeFileActions}
              />
            )}
          </ClientOnly>
          <div className={classNames(styles.Chat, 'mt-16 flex flex-col flex-grow min-w-[var(--chat-min-width)] h-fit')}>
            {!chatStarted && (
              <div id="intro" className="flex flex-col items-center justify-center w-chat mx-auto">
                <img src={Logo} alt="Alpha" className="flex flex-col items-center justify-center w-68 mx-auto" />
                <p className="text-2xl text-alpha-elements-textSecondary mb-6 sm:mb-8 font-inter">
                  Build at the speed of thought.
                </p>
                <motion.div
                  className="w-fit flex items-center justify-between px-4 py-2 gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  {isDev && (
                    <Dropdown
                      id="aiModel-select"
                      options={aiModelOptions}
                      value={selectionStore.get().aiModel}
                      onChange={(value) => {
                        selectionStore.setKey('aiModel', value as AI_Model);
                      }}
                      label="AI Model"
                      className="w-56"
                    />
                  )}
                  <Dropdown
                    id="framework-select"
                    options={frameworkOptions}
                    value={selectedFramework}
                    onChange={(value) => {
                      const framework = value as Framework;

                      const config = FRAMEWORK_CONFIGS[framework];
                      selectionStore.setKey('framework', framework);

                      const currentLang = selectionStore.get().language;

                      if (!config.languages.includes(currentLang)) {
                        selectionStore.setKey('language', config.defaultLanguage);
                      }
                    }}
                    label="Framework"
                    className="w-56"
                  />
                  <Dropdown
                    id="language-select"
                    options={[
                      {
                        value: 'ts',
                        label: 'TypeScript',
                        icon: (
                          <div className="w-5 h-5 flex items-center justify-center">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M23.429 3.50909e-06H0.570001C0.495063 3.39377e-06 0.420861 0.01478 0.35164 0.0434876C0.282419 0.0721953 0.219539 0.114271 0.166596 0.167306C0.113654 0.220341 0.0716885 0.283296 0.0431022 0.352567C0.0145159 0.421838 -0.000130591 0.496066 8.78816e-07 0.571003V23.43C-0.000130854 23.505 0.0145486 23.5793 0.0431974 23.6487C0.0718461 23.718 0.113901 23.781 0.166949 23.8341C0.219998 23.8871 0.282996 23.9292 0.352333 23.9578C0.421669 23.9865 0.495979 24.0011 0.571001 24.001H23.43C23.505 24.0011 23.5793 23.9865 23.6487 23.9578C23.718 23.9292 23.781 23.8871 23.8341 23.8341C23.8871 23.781 23.9292 23.718 23.9578 23.6487C23.9865 23.5793 24.0011 23.505 24.001 23.43V0.570004C24.001 0.494981 23.9862 0.420697 23.9574 0.351411C23.9287 0.282125 23.8865 0.219199 23.8333 0.166244C23.7802 0.113288 23.7171 0.0713441 23.6477 0.0428168C23.5784 0.0142895 23.504 -0.000259726 23.429 3.50909e-06ZM14.286 12.826H11.429V21.714H9.143V12.826H6.286V10.857H14.286V12.826ZM14.926 21.206V18.831C14.926 18.831 16.224 19.809 17.781 19.809C19.338 19.809 19.278 18.791 19.278 18.651C19.278 17.174 14.866 17.174 14.866 13.9C14.866 9.448 21.295 11.205 21.295 11.205L21.215 13.321C21.215 13.321 20.137 12.602 18.919 12.602C17.701 12.602 17.262 13.182 17.262 13.8C17.262 15.397 21.714 15.238 21.714 18.452C21.714 23.402 14.926 21.207 14.926 21.207"
                                fill="white"
                              />
                            </svg>
                          </div>
                        ),
                        disabled: !FRAMEWORK_CONFIGS[selectedFramework].languages.includes('ts' as Language),
                      },
                      {
                        value: 'js',
                        label: 'JavaScript',
                        icon: (
                          <div className="w-5 h-5 flex items-center justify-center">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M0 0H24V24H0V0ZM18.347 20.12C17.234 20.12 16.605 19.54 16.122 18.75L14.289 19.815C14.951 21.123 16.304 22.121 18.399 22.121C20.541 22.121 22.136 21.009 22.136 18.978C22.136 17.095 21.054 16.258 19.138 15.435L18.574 15.194C17.606 14.774 17.187 14.501 17.187 13.824C17.187 13.277 17.607 12.858 18.267 12.858C18.914 12.858 19.331 13.131 19.718 13.824L21.474 12.697C20.731 11.39 19.701 10.891 18.267 10.891C16.253 10.891 14.964 12.179 14.964 13.871C14.964 15.706 16.044 16.575 17.672 17.268L18.236 17.51C19.265 17.96 19.878 18.234 19.878 19.007C19.878 19.653 19.281 20.12 18.347 20.12ZM9.607 20.105C8.832 20.105 8.509 19.575 8.155 18.945L6.319 20.057C6.851 21.183 7.897 22.117 9.702 22.117C11.701 22.117 13.07 21.054 13.07 18.719V11.019H10.815V18.689C10.815 19.816 10.347 20.105 9.606 20.105"
                                fill="white"
                              />
                            </svg>
                          </div>
                        ),
                        disabled: !FRAMEWORK_CONFIGS[selectedFramework].languages.includes('js' as Language),
                      },
                    ]}
                    value={selectionStore.get().language}
                    onChange={(value) => {
                      if (isLanguageKey(value)) {
                        selectionStore.setKey('language', value);
                      }
                    }}
                    label="Language"
                    className="w-48"
                  />
                </motion.div>
              </div>
            )}
            <div
              className={classNames('pt-6 pr-6 ', {
                'h-screen min-w-[430px] flex flex-col fixed -right-4 bottom-2 z-10 bg-alpha-background-editor-secondary':
                  chatStarted,
              })}
            >
              {chatStarted && (
                <RightTopBar promptSubmited={promptSubmited} isStreaming={isStreaming} netlifyConfig={netlifyConfig} />
              )}

              <div
                ref={scrollRef}
                className={classNames({
                  'overflow-y-auto mr-2 mb-2': chatStarted,
                })}
                style={{
                  scrollbarWidth: 'auto',
                  scrollbarColor: 'var(--alpha-background-tertiary) var(--alpha-background-editor-primary)',
                }}
              >
                <ClientOnly>
                  {() => {
                    console.log('messages >>>>', messages, chatStarted);

                    return chatStarted ? (
                      <Messages
                        selectedFramework={selectedFramework}
                        ref={messageRef}
                        className="flex flex-col w-full flex-1 max-w-chat px-4 pt-4 mx-auto rounded-2xl rounded-t-none z-1 bg-alpha-background-editor-secondary pb-60"
                        messages={messages}
                        isStreaming={isStreaming}
                      />
                    ) : null;
                  }}
                </ClientOnly>
              </div>
              <div
                className={classNames('h-fit mx-auto z-prompt rounded-lg p-[1px]', {
                  'sticky bottom-0 w-[24rem] transition-all': chatStarted,
                  ' w-[30rem]': !chatStarted,
                  'bottom-[5px] left-0 right-[25px]': chatStarted,
                })}
                style={{
                  background: chatStarted ? 'transparent' : 'linear-gradient(180deg, #ffffff85 0%, #3c3c3c 100%)',
                  position: chatStarted ? 'absolute' : 'relative',
                }}
              >
                <div
                  className={classNames('shadow-sm  backdrop-filter backdrop-blur-[8px] rounded-lg', {
                    'bg-[#111113]': !chatStarted,
                    'bg-alpha-background-editor-primary': chatStarted, // secondary
                  })}
                >
                  {showSuccessIcon && <div className="h-2"></div>}
                  <textarea
                    ref={textareaRef}
                    placeholder="What would you like to build? Start typing or choose an example..."
                    className="w-full bg-transparent min-h-[100px] px-4 pt-4 focus:outline-none resize-none text-md text-alpha-elements-textPrimary placeholder-alpha-elements"
                    aria-label="Build prompt input"
                    onPaste={handlePaste}
                    onKeyDown={(event) => {
                      handleKeyDown(event);
                    }}
                    value={input}
                    disabled={isStreaming || promptSubmited || loading || showImageSuccessIcon}
                    onChange={(event) => {
                      event.preventDefault();

                      // temporily kept v large limit to avoid issues with large inputs.
                      if (event.target.value.length <= 50000) {
                        handleInputChange?.(event);
                      }
                    }}
                    style={{
                      minHeight: TEXTAREA_MIN_HEIGHT,
                      maxHeight: TEXTAREA_MAX_HEIGHT,
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'var(--alpha-background-tertiary) var(--alpha-background-editor-primary)',
                      cursor: isStreaming || promptSubmited || loading || showImageSuccessIcon ? 'not-allowed' : 'text',
                    }}
                    translate="no"
                  />
                  {input.length >= 1800 && (
                    <a
                      href="https://alphadocs.dualite.dev/prompt-techniques/prompt-techniques"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-300 hover:text-gray-100 w-full flex items-center justify-end text-xs pr-4 pt-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="i-ph:link-bold text-md text-gray-100 w-8 -mr-1" />
                      Please Read Prompting best practices for better results.
                    </a>
                  )}
                  {(attachedImage || showSuccessIcon || input.length > 0) && (
                    <div className="flex align-baseline justify-between gap-2 px-4 pb-2 text-xs">
                      <div className="flex items-center gap-1 ">
                        {attachedImage && (
                          <DelayedTooltip
                            content={
                              <div>
                                <div className="flex justify-between items-center px-3 py-2 bg-alpha-background-tertiary">
                                  <div className="flex items-center">
                                    <div
                                      className={`${extractedDesign ? 'i-ph:figma-logo-light' : 'i-ph:image-square-light'} text-alpha-text-muted mr-1.5 text-sm`}
                                    />
                                    <h3 className="text-xs font-medium text-alpha-text-white">
                                      {extractedDesign ? 'Figma Design' : 'Attached Image'}
                                    </h3>
                                  </div>
                                  {!extractedDesign && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveAttachedImage?.();
                                        setShowFigmaErrorIcon(false);
                                      }}
                                      className="flex items-center justify-center w-fit h-5 rounded-full hover:bg-alpha-background-overlay-light transition-colors duration-150 px-2"
                                    >
                                      <span className="text-alpha-text-muted mr-1">Detach Image</span>
                                      <div className="i-ph:x-light text-xs text-alpha-text-muted" />
                                    </button>
                                  )}
                                </div>
                                <div className="px-3 py-2 bg-alpha-background-tertiary">
                                  <div className="flex flex-col gap-2">
                                    <div className="relative w-full h-32 overflow-hidden rounded">
                                      <img
                                        src={attachedImage}
                                        alt="Attached"
                                        className="object-contain w-full h-full"
                                      />
                                    </div>
                                    {showFigmaErrorIcon && (
                                      <div className="text-xs text-red-500 bg-red-500/10 p-2 rounded">
                                        This design is too large to use. Please select a smaller design.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            }
                            delay={1000}
                            position="top"
                          >
                            <div className="flex items-center bg-alpha-background-editor-secondary px-2 py-1 rounded cursor-pointer">
                              <div
                                className={`${extractedDesign ? 'i-ph:figma-logo-light' : 'i-ph:image-square-light'} text-xs mr-1 ${
                                  showFigmaErrorIcon ? 'text-red-500' : 'text-green-500'
                                }`}
                              />
                              <span className="text-alpha-elements-textSecondary">
                                {extractedDesign ? 'Figma' : 'Image'}
                              </span>
                              {showFigmaErrorIcon && <div className="i-ph:x-light text-xs ml-1 text-red-500" />}
                              {(showFigmaSuccessIcon || showImageSuccessIcon) && !showFigmaErrorIcon && (
                                <div className="i-ph:check-light text-xs ml-1 text-green-500" />
                              )}
                            </div>
                          </DelayedTooltip>
                        )}

                        {showSuccessIcon && (
                          <DelayedTooltip
                            content={
                              <div>
                                <div className="flex justify-between items-center px-3 py-2 bg-alpha-background-tertiary border-b border-alpha-border-darker">
                                  <div className="flex items-center">
                                    <div className="i-ph:code text-alpha-text-muted mr-1.5 text-sm" />
                                    <h3 className="text-xs font-medium text-alpha-text-white">API Specification</h3>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowSuccessIcon(false);
                                      setOpenApiSpec('');
                                      localStorage.removeItem('dualite_api_data');
                                      localStorage.removeItem('dualite_api_data_yaml');
                                    }}
                                    className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-alpha-background-overlay-light transition-colors duration-150"
                                  >
                                    <div className="i-ph:x-light text-xs text-alpha-text-muted" />
                                  </button>
                                </div>
                                <div className="max-h-60 overflow-auto px-3 py-2 bg-alpha-background-tertiary">
                                  {openApiSpec === 'DUALITEX_INFER_YOURSELF' ? (
                                    <div className="text-alpha-text-muted py-1 flex items-center">
                                      <div className="i-ph:lightbulb-filament text-sm mr-2 text-yellow-400" />
                                      <span>API spec will be automatically inferred from your UI</span>
                                    </div>
                                  ) : (
                                    <pre
                                      className="whitespace-pre-wrap break-all text-alpha-text-white font-mono text-xs leading-relaxed"
                                      style={{
                                        fontFamily:
                                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                                      }}
                                    >
                                      {openApiSpec}
                                    </pre>
                                  )}
                                </div>
                              </div>
                            }
                            delay={1000}
                            position="top"
                          >
                            <div className="flex items-center bg-alpha-background-editor-secondary px-2 py-1 rounded cursor-pointer">
                              <div className="i-ph:code text-xs mr-1 text-green-500" />
                              <span className="text-alpha-elements-textSecondary text-xs">API 1</span>
                            </div>
                          </DelayedTooltip>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {input.length > 1 && (
                          <div className="text-[8px] leading-none text-alpha-elements-textTertiary flex items-center">
                            Use <kbd className="kdb">Shift</kbd> + <kbd className="kdb">Return</kbd> for a new line
                          </div>
                        )}
                        <div
                          className={`text-[8px] leading-none ${input.length >= 1800 ? 'text-red-400' : 'text-alpha-elements-textTertiary'}`}
                        >
                          {input.length}/2000
                        </div>
                      </div>
                    </div>
                  )}
                  <hr className="bg-[#414142] mx-4 h-[1px] border-none" />
                  <div className="flex justify-between text-sm p-4 pt-2">
                    <div className="flex gap-2 items-center">
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                        disabled={isStreaming || promptSubmited || loading || showImageSuccessIcon}
                      />
                      <PanelButton
                        isDisabled={isStreaming || showFigmaSuccessIcon || promptSubmited || loading}
                        onClick={() => document.getElementById('image-upload')?.click()}
                        icon={
                          <div className="flex items-center">
                            <div className="i-ph:image-square-light text-2xl" />
                            {showImageSuccessIcon && <div className="i-ph:check-light text-green-500" />}
                          </div>
                        }
                        title={showImageSuccessIcon ? 'Image attached' : 'Attach image'}
                      />

                      <PanelButton
                        isDisabled={loading || showImageSuccessIcon || promptSubmited || isStreaming}
                        onClick={() => setOauthIsOpen(true)}
                        icon={
                          <div className="flex items-center">
                            <div className="i-ph:figma-logo-light text-2xl" />
                            {showFigmaSuccessIcon && <div className="i-ph:check-light text-green-500" />}
                            {showFigmaErrorIcon && <div className="i-ph:x-light text-red-500" />}
                          </div>
                        }
                        title={
                          showFigmaSuccessIcon
                            ? 'Figma design imported'
                            : showFigmaErrorIcon
                              ? 'Figma design too large'
                              : 'Import from Figma'
                        }
                      />
                      {showSuccessIcon ? (
                        <PanelButton
                          onClick={() => setIsCodeIntegrationOpen(true)}
                          isDisabled={promptSubmited || isStreaming || loading}
                          icon={
                            <div className="flex items-center">
                              <div className="i-ph:code-light text-2xl" />
                              <div className="i-ph:check-light text-green-500" />
                            </div>
                          }
                          title="View API Specification"
                        />
                      ) : (
                        <PanelButton
                          isDisabled={isStreaming == null || promptSubmited || isStreaming || loading}
                          onClick={() => setIsCodeIntegrationOpen(true)}
                          icon={<div className="i-ph:code-light text-2xl" />}
                          title="Attach API Specification"
                        />
                      )}

                      {projectRules ? (
                        <PanelButton
                          onClick={() => setShowRulesModal(true)}
                          isDisabled={promptSubmited || isStreaming || loading}
                          icon={
                            <div className="flex items-center">
                              <div className="i-ph:notebook text-2xl" />
                              <div className="i-ph:check-light text-green-500" />
                            </div>
                          }
                          title="Edit Project Rules"
                        />
                      ) : (
                        <PanelButton
                          isDisabled={isStreaming || promptSubmited || loading}
                          onClick={() => setShowRulesModal(true)}
                          icon={<div className="i-ph:notebook text-2xl" />}
                          title="Add Project Rules"
                        />
                      )}
                      {/* <PanelButton
                        isDisabled={loading || showImageSuccessIcon || promptSubmited || isStreaming}
                        onClick={() => setGithubModalIsOpen?.(true)}
                        icon={
                          <div className="flex items-center">
                            <div className="i-ph:github-logo-light text-2xl" />
                          </div>
                        }
                        title="Import from Github"
                      /> */}
                    </div>
                    <ClientOnly>
                      {() => (
                        <SendButton
                          show={input.length > 0 || isStreaming}
                          isStreaming={isStreaming}
                          isDisabled={(showFigmaErrorIcon && !isStreaming) || promptSubmited}
                          promptSubmited={promptSubmited}
                          onClick={(event) => {
                            if (isStreaming) {
                              handleStop?.();
                              return;
                            }

                            if (showFigmaErrorIcon) {
                              toast.error('Please remove the invalid Figma design before sending');
                              return;
                            }

                            sendMessage?.(event);
                          }}
                        />
                      )}
                    </ClientOnly>
                  </div>
                </div>
              </div>
            </div>
            {!chatStarted && (
              <div id="examples" className="bg-transparent w-fit mx-auto">
                <div className="text-center my-12">
                  <h2 className="text-xl font-semibold text-gray-200 mb-2">Start with an example</h2>
                  <p className="text-sm text-gray-400">Select one of these examples or type your own request</p>
                </div>
                <div className="mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-10">
                  {EXAMPLE_PROMPTS.map((prompt, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        if (handleInputChange && textareaRef?.current) {
                          textareaRef.current.value = prompt.text;

                          const event = {
                            target: textareaRef.current,
                          } as React.ChangeEvent<HTMLTextAreaElement>;

                          handleInputChange(event);
                        }

                        sendMessage?.(e, prompt.text);
                      }}
                      className="p-4 bg-zinc-800/80 hover:bg-zinc-700 rounded-xl text-left transition-all duration-200 border border-zinc-700/50 hover:border-zinc-600 shadow-md hover:shadow-lg hover:-translate-y-0.5 group"
                    >
                      <div className="flex items-start">
                        <div className="mr-3 mt-0.5 text-gray-400 group-hover:text-indigo-300 transition-colors">
                          <div className={`${prompt.icon} text-xl`}></div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-200 font-medium group-hover:text-white">{prompt.text}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {isCodeIntegrationOpen && (
            <ClientOnly>
              {() => (
                <CodeIntegration
                  isOpen={isCodeIntegrationOpen}
                  onClose={() => setIsCodeIntegrationOpen(false)}
                  onSuccessIconUpdate={() => setShowSuccessIcon(true)}
                  onSpecUpdate={(spec) => setOpenApiSpec(spec)}
                  selectedFramework={selectedFramework}
                />
              )}
            </ClientOnly>
          )}
        </div>

        {/* Replace the Help Button and Menu with the new component */}
        {!chatStarted && (
          <div className="">
            <HelpMenu position="bottom-right" withBg={false} />
          </div>
        )}

        <OauthModal
          isOpen={oauthIsOpen}
          onRemoveAttachedImage={
            onRemoveAttachedImage ??
            (() => {
              return null;
            })
          }
          onClose={() => {
            setOauthIsOpen(false);
            console.log('OAuth modal closed');

            if (extractedDesign?.image && !showFigmaErrorIcon) {
              onAttachImage?.(extractedDesign.image);
              setShowFigmaSuccessIcon(true);
              setShowFigmaErrorIcon(false);
              setShowImageSuccessIcon(false);
            }
          }}
          onExtractComplete={(data) => {
            console.log('>>>[chat] onExtractComplete: ', data);
            onExtractComplete?.(data);
          }}
          onExtractError={handleFigmaExtractError}
        />

        <ProjectRulesModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />
      </div>
    );
  },
);
